import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ImageIcon from "@mui/icons-material/Image";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import moment from "moment";
import { GetClientWalletStatement } from "../../Service/_index";
import NoData from "../../Resources/Images/NoDataNavy.png";
import { formatINR } from "../AddMoneyToUser/addMoneyUtils";
import { outlinedButtonSx } from "../AddMoneyToUser/parts";
import Styles from "../AddMoneyToUser/AddMoneyToUser.module.scss";
import { TONE_COLORS, formatSigned } from "../AdminPolicyWallet/walletStatementUtils";
import { breakdownText, describeEntry, entryTone, hasSingleBalance, kindLabel, partLabel } from "./walletEntryUtils";

const COLUMNS = ["", "Date", "Company", "Type", "Amount", "Balance After", "Details"];
const PAGE_SIZES = [10, 25, 50, 100];
const AMOUNT_COLORS = { debit: "#B42318", credit: "#12762D", neutral: "#667085" };

const Dash = () => <span style={{ color: "#98A2B3" }}>—</span>;
const dateText = (value) => (value ? moment(value).format("D MMM YYYY, h:mm A") : "--");

const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, row) => (
    <TableRow key={row}>
      {COLUMNS.map((label, i) => (
        <TableCell key={`${label}-${i}`} className={Styles.bodyCell}>
          <Skeleton variant="text" width="80%" />
        </TableCell>
      ))}
    </TableRow>
  ));

// The rows of one entry, shown when it is opened: each company's own row with its own balance after.
const Legs = ({ entry }) => (
  <Box sx={{ backgroundColor: "#F9FAFB", px: 2, py: 1.25, borderBottom: "1px solid #EAECF0" }}>
    <Table size="small" aria-label="Rows of this entry">
      <TableHead>
        <TableRow>
          {["Time", "Company", "Part", "Amount", "Balance After", "Remark"].map((label) => (
            <TableCell key={label} sx={{ fontFamily: "Inter", fontSize: "12px", fontWeight: 600, color: "#475467" }}>
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {entry.legs.map((leg) => (
          <TableRow key={leg._id}>
            <TableCell sx={{ whiteSpace: "nowrap", fontSize: "12.5px" }}>
              {leg.createdAt ? moment(leg.createdAt).format("h:mm:ss A") : "--"}
            </TableCell>
            <TableCell sx={{ fontSize: "12.5px" }}>{leg.companyName}</TableCell>
            <TableCell sx={{ fontSize: "12.5px" }}>{partLabel(leg) || <Dash />}</TableCell>
            <TableCell
              sx={{
                whiteSpace: "nowrap",
                fontSize: "12.5px",
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                color: leg.signedAmount < 0 ? AMOUNT_COLORS.debit : AMOUNT_COLORS.credit,
              }}
            >
              {formatSigned(leg.signedAmount)}
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap", fontSize: "12.5px", fontVariantNumeric: "tabular-nums" }}>
              {leg.balanceAfter === null || leg.balanceAfter === undefined ? <Dash /> : formatINR(leg.balanceAfter)}
            </TableCell>
            <TableCell sx={{ fontSize: "12.5px", color: "#667085", overflowWrap: "anywhere" }}>
              {leg.remarks || <Dash />}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

/**
 * The Company Wallet statement of one client (all insurers, or one). One row per EVENT: a policy creation shows its
 * insurers' rows as one entry, an edit is its own entry, a top-up / removal stands alone. Pages come from the server
 * and are counted in entries, so a policy is never split across pages.
 *
 * All state lives here, so mount it again (a new `key`) to start over: the parent does that whenever the tab, the
 * grouping switch or the wallet changes, which is also what puts the page back on the first one.
 */
const WalletStatementTable = ({ clientId, companyName = null, grouped = true, onOpenPolicy, onViewReceipt }) => {
  const [page, setPage] = useState(0); // 0-based for the pagination control; the API is 1-based
  const [limit, setLimit] = useState(25);
  const [reloadKey, setReloadKey] = useState(0);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const latestRequest = useRef(0);

  useEffect(() => {
    const id = ++latestRequest.current;
    setLoading(true);
    setError("");
    GetClientWalletStatement(clientId, { companyName, page: page + 1, limit, grouped }).then((res) => {
      if (id !== latestRequest.current) return; // a newer request (or an unmount) superseded this one
      setLoading(false);
      if (res.error) {
        setError(res.message || "Could not load the statement");
        return;
      }
      const body = res.data || {};
      setEntries(Array.isArray(body.entries) ? body.entries : []);
      setTotal(Number(body.total) || 0);
      setHasLoaded(true);
    });
    return () => {
      latestRequest.current += 1;
    };
  }, [clientId, companyName, grouped, page, limit, reloadKey]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const showSkeleton = !hasLoaded && loading;
  const showEmpty = !error && hasLoaded && !loading && entries.length === 0;

  if (error) {
    return (
      <Box sx={{ px: 2.25, py: 2 }}>
        <Box
          role="alert"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            border: "1px solid #f3d6d6",
            borderRadius: "8px",
            backgroundColor: "#fdf7f7",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 20, color: "#b3261e" }} />
          <Typography sx={{ flex: 1, fontFamily: "Inter", fontSize: "12.5px", color: "#b3261e" }}>{error}</Typography>
          <Button size="small" variant="outlined" onClick={() => setReloadKey((n) => n + 1)} sx={outlinedButtonSx}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (showEmpty) {
    return (
      <Box className={Styles.EmptyState}>
        <img src={NoData} alt="" style={{ width: 200, height: 133, objectFit: "contain" }} />
        <Typography sx={{ color: "#1E3A5F", fontFamily: "Inter", fontSize: "16px", fontWeight: 600 }}>
          No transactions yet
        </Typography>
        <Typography sx={{ color: "#667085", fontFamily: "Inter", fontSize: "12.5px" }}>
          {companyName
            ? `Nothing has been added to or taken from the ${companyName} wallet yet.`
            : "Top-ups, policy charges and refunds will be listed here."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={Styles.tableWrap}>
      <Box sx={{ height: 3 }}>{loading && !showSkeleton && <LinearProgress sx={{ height: 3 }} />}</Box>
      <Box className={Styles.tableScroll}>
        <Table aria-label="Company wallet statement" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((label, i) => (
                <TableCell
                  key={`${label}-${i}`}
                  className={Styles.headCell}
                  align={label === "Amount" || label === "Balance After" ? "right" : "left"}
                  sx={i === 0 ? { width: 44 } : undefined}
                >
                  {i === 0 ? (
                    <Box component="span" sx={visuallyHidden}>
                      Show or hide the rows of an entry
                    </Box>
                  ) : (
                    label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className={loading && !showSkeleton ? Styles.dim : ""}>
            {showSkeleton ? (
              <SkeletonRows />
            ) : (
              entries.map((entry) => {
                const details = describeEntry(entry);
                const breakdown = breakdownText(entry);
                const tone = entryTone(entry);
                const chip = TONE_COLORS[tone];
                const several = entry.legs.length > 1;
                const isOpen = Boolean(expanded[entry.entryKey]);
                const receipts = entry.legs.filter((leg) => leg.receiptImage && leg.receiptImage.key);
                return (
                  <Fragment key={entry.entryKey}>
                    <TableRow className={Styles.row}>
                      <TableCell className={Styles.bodyCell} sx={{ width: 44, px: 0.5 }}>
                        {several && (
                          <IconButton
                            size="small"
                            aria-expanded={isOpen}
                            aria-label={`${isOpen ? "Hide" : "Show"} the ${entry.legs.length} rows of ${details.primary || "this entry"}`}
                            onClick={() => toggle(entry.entryKey)}
                          >
                            {isOpen ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell className={Styles.bodyCell} sx={{ whiteSpace: "nowrap" }}>
                        {dateText(entry.at)}
                      </TableCell>
                      <TableCell className={Styles.bodyCell}>{entry.companies.join(" · ")}</TableCell>
                      <TableCell className={Styles.bodyCell}>
                        <Chip
                          size="small"
                          label={kindLabel(entry.kind)}
                          sx={{
                            height: 22,
                            fontFamily: "Inter",
                            fontSize: "11.5px",
                            fontWeight: 600,
                            color: chip.color,
                            backgroundColor: chip.bg,
                            border: `1px solid ${chip.border}`,
                          }}
                        />
                        {several && (
                          <Typography sx={{ mt: 0.25, fontSize: "11.5px", color: "#667085" }}>
                            {entry.legs.length} rows
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={Styles.bodyCell}
                        sx={{
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                          color: `${AMOUNT_COLORS[tone]} !important`,
                        }}
                      >
                        {formatSigned(entry.net)}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={Styles.bodyCell}
                        sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
                      >
                        {hasSingleBalance(entry) ? (
                          formatINR(entry.balanceAfter)
                        ) : (
                          <Tooltip title="Each company has its own balance. Open this row to see them." arrow>
                            <span>
                              <Dash />
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell className={Styles.bodyCell} sx={{ maxWidth: 420 }}>
                        <Box
                          sx={{
                            fontWeight: 500,
                            color: details.muted ? "#98A2B3" : "#1E3A5F",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {!details.primary ? (
                            <Dash />
                          ) : details.openable && onOpenPolicy ? (
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={() => onOpenPolicy(entry.referenceId)}
                              aria-label={`Open policy ${entry.policy.policyId || ""}`.trim()}
                              sx={{ font: "inherit", fontWeight: 500, color: "#1E3A5F", textAlign: "left" }}
                            >
                              {details.primary}
                            </Link>
                          ) : (
                            details.primary
                          )}
                        </Box>
                        {details.secondary && (
                          <Box sx={{ fontSize: "12px", color: "#667085", overflowWrap: "anywhere" }}>
                            {details.secondary}
                          </Box>
                        )}
                        {breakdown && (
                          <Box sx={{ mt: 0.25, fontSize: "12px", color: "#344054", overflowWrap: "anywhere" }}>
                            {breakdown}
                          </Box>
                        )}
                        {receipts.map((leg) => (
                          <Link
                            key={leg._id}
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => onViewReceipt && onViewReceipt(leg.receiptImage)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.25,
                              fontSize: "12px",
                              color: "#1E3A5F",
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 13 }} /> View Receipt
                          </Link>
                        ))}
                      </TableCell>
                    </TableRow>
                    {several && (
                      <TableRow>
                        <TableCell colSpan={COLUMNS.length} sx={{ p: 0, border: 0 }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Legs entry={entry} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
      <Box className={Styles.pagination}>
        <TablePagination
          component="div"
          rowsPerPageOptions={PAGE_SIZES}
          count={total}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(event) => {
            setLimit(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Box>
    </Box>
  );
};

export default WalletStatementTable;
